import { Link } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      // Always show success — never reveal whether email exists
      setSent(true);
    } catch {
      setError("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-950">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/login-bg.jpg')" }}
      />
      <div className="absolute inset-0 bg-slate-950/75" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />

      <div className="relative z-10 w-full max-w-md px-4">
        <div className="mb-8 flex flex-col items-center">
          <img
            src="/logo-white.png"
            alt="Prominent Builders"
            className="h-auto w-72 drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
          />
        </div>

        <Card className="border-slate-700/50 bg-slate-800/80 shadow-2xl backdrop-blur">
          <CardContent className="pt-6">
            {sent ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="rounded-full bg-green-500/10 p-3 ring-1 ring-green-500/20">
                  <CheckCircle2 className="size-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Check your inbox</h3>
                <p className="text-sm text-slate-400">
                  If <span className="font-medium text-white">{email}</span> is registered, you'll
                  receive a reset link shortly.
                </p>
                <Link
                  to="/login"
                  className="mt-4 flex items-center gap-1.5 text-sm text-[#778DA9] hover:underline"
                >
                  <ArrowLeft className="size-3.5" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-[#415A77]/20 p-2">
                    <Lock className="size-4 text-[#778DA9]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Forgot password?</h2>
                    <p className="text-xs text-slate-400">
                      Enter your email to receive a reset link
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-400 ring-1 ring-red-500/20">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#415A77] text-white hover:bg-[#4E6B8A]"
                >
                  {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                  Send Reset Link
                </Button>

                <Link
                  to="/login"
                  className="flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-300"
                >
                  <ArrowLeft className="size-3.5" />
                  Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
